import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCompanyStore } from "../store/slices/company";
import { API_ENDPOINTS } from "../constants/api_endpoints";
import axiosInstance from "./axios";

// ── Default labels for all 22 term_keys ──────────────────────────────────────
export const TERM_DEFAULTS: Record<string, { s: string; p: string }> = {
  job:         { s: "Job",          p: "Jobs"          },
  worker:      { s: "Worker",       p: "Workers"       },
  client:      { s: "Client",       p: "Clients"       },
  location:    { s: "Site",         p: "Sites"         },
  quote:       { s: "Quote",        p: "Quotes"        },
  invoice:     { s: "Invoice",      p: "Invoices"      },
  task:        { s: "Task",         p: "Tasks"         },
  team:        { s: "Team",         p: "Teams"         },
  supervisor:  { s: "Supervisor",   p: "Supervisors"   },
  asset:       { s: "Asset",        p: "Assets"        },
  contract:    { s: "Contract",     p: "Contracts"     },
  report:      { s: "Report",       p: "Reports"       },
  inspection:  { s: "Inspection",   p: "Inspections"   },
  certificate: { s: "Certificate",  p: "Certificates"  },
  schedule:    { s: "Schedule",     p: "Schedules"     },
  material:    { s: "Material",     p: "Materials"     },
  incident:    { s: "Incident",     p: "Incidents"     },
  visit:       { s: "Visit",        p: "Visits"        },
  project:     { s: "Project",      p: "Projects"      },
  supplier:    { s: "Supplier",     p: "Suppliers"     },
  ticket:      { s: "Ticket",       p: "Tickets"       },
  note:        { s: "Note",         p: "Notes"         },
};

interface TerminologyContextValue {
  /** term("job") → "Treatment", term("job", true) → "Treatments" */
  term: (key: string, plural?: boolean) => string;
  termMap: Record<string, { s: string; p: string }>;
  industryName: string | null;
  isLoading: boolean;
}

const TerminologyContext = createContext<TerminologyContextValue>({
  term: (key, plural) => plural ? (TERM_DEFAULTS[key]?.p ?? key) : (TERM_DEFAULTS[key]?.s ?? key),
  termMap: TERM_DEFAULTS,
  industryName: null,
  isLoading: false,
});

async function fetchIndustryWithTerminology(industryId: number) {
  const res = await axiosInstance.get(`${API_ENDPOINTS.INDUSTRY_DETAIL}/${industryId}`);
  return res.data?.data ?? null;
}

export function TerminologyProvider({ children }: { children: React.ReactNode }) {
  const { company } = useCompanyStore();

  const primaryIndustry =
    company?.industries?.find((i) => i.is_primary) ?? company?.industries?.[0] ?? null;

  const { data: industryData, isLoading } = useQuery({
    queryKey: ["industry-terminology", primaryIndustry?.id],
    queryFn: () => fetchIndustryWithTerminology(primaryIndustry!.id),
    enabled: !!primaryIndustry?.id,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const termMap = useMemo<Record<string, { s: string; p: string }>>(() => {
    const map = { ...TERM_DEFAULTS };
    const terms: Array<{ term_key: string; term_singular: string; term_plural: string }> =
      industryData?.terminology ?? [];
    for (const t of terms) {
      if (t.term_singular || t.term_plural) {
        map[t.term_key] = { s: t.term_singular || map[t.term_key]?.s, p: t.term_plural || map[t.term_key]?.p };
      }
    }
    return map;
  }, [industryData]);

  const term = (key: string, plural = false): string =>
    plural ? (termMap[key]?.p ?? key) : (termMap[key]?.s ?? key);

  return (
    <TerminologyContext.Provider
      value={{ term, termMap, industryName: primaryIndustry?.name ?? null, isLoading }}
    >
      {children}
    </TerminologyContext.Provider>
  );
}

/** Returns industry-aware label helper. Use: `const { term } = useTerminology()` */
export function useTerminology() {
  return useContext(TerminologyContext);
}

/** Shorthand — just get the term function directly */
export function useTerm() {
  return useContext(TerminologyContext).term;
}
